import axios, { HttpStatusCode } from "axios";
import { customFetch } from "../lib/network/Axios";
import {
  KioskPerformanceFilters,
  KioskPayoutLedgerEntry,
  KioskPayoutLedgerResponse,
  OwnerPayoutProfile,
  OwnerPayoutRequest,
  OwnerPayoutRequestStatus,
  OwnerPayoutRequestType,
  KioskPerformanceSummaryRow,
  KioskOwnerPreferences,
  KioskTransactionHistoryRow,
  KioskTransactionsHistoryResponse,
  KioskTransactionsPagination,
  KioskTransactionsSummary,
} from "../types/kioskTransactions.type";

const getNumber = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const toOptionalString = (value: unknown): string | undefined => {
  return typeof value === "string" && value.trim() ? value : undefined;
};

const extractApiErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data;

    if (typeof responseData?.message === "string" && responseData.message.trim()) {
      return responseData.message;
    }

    if (Array.isArray(responseData?.message) && responseData.message.length > 0) {
      const firstMessage = responseData.message[0];
      if (typeof firstMessage === "string" && firstMessage.trim()) {
        return firstMessage;
      }
    }

    if (typeof responseData === "string" && responseData.trim()) {
      return responseData;
    }

    if (typeof error.message === "string" && error.message.trim()) {
      return error.message;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
};

const buildQueryParams = (
  filters: KioskPerformanceFilters,
  page?: number,
  limit?: number,
): URLSearchParams => {
  const params = new URLSearchParams();

  if (filters.kioskId?.trim()) {
    params.set("kioskId", filters.kioskId.trim());
  }

  if (filters.status?.trim()) {
    params.set("status", filters.status.trim());
  }

  if (filters.fromDate?.trim()) {
    params.set("fromDate", filters.fromDate.trim());
  }

  if (filters.toDate?.trim()) {
    params.set("toDate", filters.toDate.trim());
  }

  if (typeof page === "number") {
    params.set("page", String(page));
  }

  if (typeof limit === "number") {
    params.set("limit", String(limit));
  }

  return params;
};

const mapSummaryRow = (item: any): KioskPerformanceSummaryRow => {
  return {
    kioskId: toOptionalString(item?.kioskId) || toOptionalString(item?.kiosk?.kioskId) || "-",
    kioskName: toOptionalString(item?.kioskName) || toOptionalString(item?.kiosk?.name) || "-",
    kioskLocation:
      toOptionalString(item?.kioskLocation) || toOptionalString(item?.kiosk?.location),
    totalTransactions: getNumber(item?.totalTransactions),
    successfulTransactions:
      getNumber(item?.successfulTransactions) || getNumber(item?.receivedTransactions),
    failedTransactions:
      getNumber(item?.failedTransactions) || getNumber(item?.incompleteTransactions),
    pendingTransactions: getNumber(item?.pendingTransactions),
    totalReceivedBase: getNumber(item?.totalReceivedBase),
    totalUnits: getNumber(item?.totalUnits),
    lastTransactionAt: toOptionalString(item?.lastTransactionAt),
  };
};

const getBoolean = (value: unknown, fallback = false): boolean => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true" || normalized === "1") {
      return true;
    }
    if (normalized === "false" || normalized === "0") {
      return false;
    }
  }

  return fallback;
};

const resolveOwnerPreferences = (payload: any): KioskOwnerPreferences => {
  const settings = payload?.ownerPreferences || {};

  return {
    reportLiveEnabled: getBoolean(settings?.reportLiveEnabled, false),
    reportWeeklyEnabled: getBoolean(settings?.reportWeeklyEnabled, true),
    reportMonthlyEnabled: getBoolean(settings?.reportMonthlyEnabled, false),
    payoutMode: settings?.payoutMode === "CASH" ? "CASH" : "PROGRAM",
    payoutProgramKey:
      toOptionalString(settings?.payoutProgramKey) || "humanitarian-food-programmes",
    payoutProgramName:
      toOptionalString(settings?.payoutProgramName) || "Humanitarian food programmes",
  };
};

const mapHistoryRow = (item: any, index: number): KioskTransactionHistoryRow => {
  const chargedAmount = getNumber(item?.chargedAmount);
  const requestedAmount = getNumber(item?.requestedAmount);

  return {
    id:
      toOptionalString(item?.id) ||
      toOptionalString(item?.stripeId) ||
      `${toOptionalString(item?.createdAt) || "row"}-${index}`,
    createdAt: toOptionalString(item?.createdAt),
    kioskId: toOptionalString(item?.kioskId),
    kioskName: toOptionalString(item?.kioskName),
    campaignName: toOptionalString(item?.campaignName),
    stripeId: toOptionalString(item?.stripeId),
    requestedAmount,
    requestedCurrency: toOptionalString(item?.requestedCurrency),
    chargedAmount,
    chargedCurrency: toOptionalString(item?.chargedCurrency),
    chargedAmountBase: getNumber(item?.usdAmount) || getNumber(item?.chargedAmountBase),
    numberOfUnits: getNumber(item?.numberOfUnits),
    status: toOptionalString(item?.status),
    finalDonationStatus: toOptionalString(item?.finalDonationStatus),
    error: toOptionalString(item?.error),
  };
};

export const getKioskTransactionsSummary = async (
  filters: KioskPerformanceFilters,
): Promise<KioskTransactionsSummary> => {
  try {
    const params = buildQueryParams(filters);
    const query = params.toString();
    const response = await customFetch.get(
      `/kiosks/transactions/summary${query ? `?${query}` : ""}`,
    );

    if (response?.status !== HttpStatusCode.Ok) {
      throw new Error("Failed to fetch kiosk transaction summary");
    }

    const payload = response.data || {};
    const rowsSource = Array.isArray(payload?.perKioskPerformance)
      ? payload.perKioskPerformance
      : Array.isArray(payload?.data?.perKioskPerformance)
        ? payload.data.perKioskPerformance
        : Array.isArray(payload?.kiosks)
          ? payload.kiosks
          : [];

    const perKioskPerformance: KioskPerformanceSummaryRow[] = rowsSource.map(mapSummaryRow);

    const totalTransactions =
      getNumber(payload?.totalTransactions) ||
      getNumber(payload?.kpis?.totalTransactions) ||
      perKioskPerformance.reduce((acc: number, row) => acc + row.totalTransactions, 0);

    const successful =
      getNumber(payload?.successful) ||
      getNumber(payload?.successfulTransactions) ||
      getNumber(payload?.kpis?.successful) ||
      perKioskPerformance.reduce((acc: number, row) => acc + row.successfulTransactions, 0);

    const failed =
      getNumber(payload?.failed) ||
      getNumber(payload?.failedTransactions) ||
      getNumber(payload?.kpis?.failed) ||
      perKioskPerformance.reduce((acc: number, row) => acc + row.failedTransactions, 0);

    const pending =
      getNumber(payload?.pending) ||
      getNumber(payload?.pendingTransactions) ||
      getNumber(payload?.kpis?.pending) ||
      perKioskPerformance.reduce((acc: number, row) => acc + row.pendingTransactions, 0);

    const totalReceived =
      getNumber(payload?.totalReceived) ||
      getNumber(payload?.totalReceivedBase) ||
      getNumber(payload?.kpis?.totalReceived) ||
      perKioskPerformance.reduce((acc: number, row) => acc + row.totalReceivedBase, 0);

    const totalUnits =
      getNumber(payload?.totalUnits) ||
      getNumber(payload?.kpis?.totalUnits) ||
      perKioskPerformance.reduce((acc: number, row) => acc + row.totalUnits, 0);

    return {
      kpis: {
        totalTransactions,
        successful,
        failed,
        pending,
        totalReceived,
        totalUnits,
      },
      impact: {
        totalFoodBars:
          getNumber(payload?.impact?.totalFoodBars) ||
          getNumber(payload?.totals?.totalUnits) ||
          totalUnits,
        totalAmountUsd:
          getNumber(payload?.impact?.totalAmountUsd) ||
          getNumber(payload?.totals?.totalReceivedBase) ||
          totalReceived,
        byChannel: {
          DIGITAL: {
            foodBars: getNumber(payload?.impact?.byChannel?.DIGITAL?.foodBars),
            amountUsd: getNumber(payload?.impact?.byChannel?.DIGITAL?.amountUsd),
          },
          PHYSICAL: {
            foodBars: getNumber(payload?.impact?.byChannel?.PHYSICAL?.foodBars),
            amountUsd: getNumber(payload?.impact?.byChannel?.PHYSICAL?.amountUsd),
          },
        },
      },
      trends: {
        daily: Array.isArray(payload?.trends?.daily)
          ? payload.trends.daily.map((point: any) => ({
              bucket: toOptionalString(point?.bucket) || "",
              totalUnits: getNumber(point?.totalUnits),
              totalUsd: getNumber(point?.totalUsd),
            }))
          : [],
        weekly: Array.isArray(payload?.trends?.weekly)
          ? payload.trends.weekly.map((point: any) => ({
              bucket: toOptionalString(point?.bucket) || "",
              totalUnits: getNumber(point?.totalUnits),
              totalUsd: getNumber(point?.totalUsd),
            }))
          : [],
        monthly: Array.isArray(payload?.trends?.monthly)
          ? payload.trends.monthly.map((point: any) => ({
              bucket: toOptionalString(point?.bucket) || "",
              totalUnits: getNumber(point?.totalUnits),
              totalUsd: getNumber(point?.totalUsd),
            }))
          : [],
      },
      topDonors: Array.isArray(payload?.topDonors)
        ? payload.topDonors.map((donor: any) => ({
            donor: toOptionalString(donor?.donor) || "Anonymous",
            amountUsd: getNumber(donor?.amountUsd),
            donationCount: getNumber(donor?.donationCount),
            lastDonationAt: toOptionalString(donor?.lastDonationAt),
            location: toOptionalString(donor?.location),
          }))
        : [],
      ownerRewards: {
        totalRewardsUsd: getNumber(payload?.ownerRewards?.totalRewardsUsd),
        totalPayableUsd: getNumber(payload?.ownerRewards?.totalPayableUsd),
        totalPayoutUsd: getNumber(payload?.ownerRewards?.totalPayoutUsd),
        balanceUsd: getNumber(payload?.ownerRewards?.balanceUsd),
      },
      ownerPreferences: resolveOwnerPreferences(payload),
      perKioskPerformance,
    };
  } catch (error) {
    throw new Error(
      extractApiErrorMessage(error, "Failed to fetch kiosk transaction summary"),
    );
  }
};

export const getKioskTransactionsHistory = async (
  filters: KioskPerformanceFilters,
  page: number,
  limit: number,
): Promise<KioskTransactionsHistoryResponse> => {
  try {
    const params = buildQueryParams(filters, page, limit);
    const query = params.toString();
    const response = await customFetch.get(
      `/kiosks/transactions${query ? `?${query}` : ""}`,
    );

    if (response?.status !== HttpStatusCode.Ok) {
      throw new Error("Failed to fetch kiosk transaction history");
    }

    const payload = response.data || {};
    const rowsSource = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.items)
        ? payload.items
      : Array.isArray(payload?.data)
        ? payload.data
        : [];

    const rows = rowsSource.map(mapHistoryRow);

    const paginationRaw = payload?.pagination || {};
    const total =
      getNumber(paginationRaw?.total) ||
      getNumber(payload?.total) ||
      rows.length;

    const responsePage = getNumber(paginationRaw?.page) || getNumber(payload?.page) || page;
    const responseLimit =
      getNumber(paginationRaw?.limit) || getNumber(payload?.limit) || limit;

    const pagination: KioskTransactionsPagination = {
      page: responsePage,
      limit: responseLimit,
      total,
      totalPages:
        getNumber(paginationRaw?.totalPages) ||
        getNumber(payload?.totalPages) ||
        Math.max(1, Math.ceil(total / Math.max(1, responseLimit))),
    };

    return {
      rows,
      pagination,
    };
  } catch (error) {
    throw new Error(
      extractApiErrorMessage(error, "Failed to fetch kiosk transaction history"),
    );
  }
};

const mapPayoutLedgerEntry = (item: any): KioskPayoutLedgerEntry => {
  return {
    key: toOptionalString(item?.key) || `${toOptionalString(item?.source) || "entry"}-${String(item?.amountCents || 0)}`,
    source: item?.source === "invoice" ? "invoice" : "ledger",
    occurredAt: toOptionalString(item?.occurredAt) || new Date().toISOString(),
    ownerEmail: toOptionalString(item?.ownerEmail) || "",
    kioskId: toOptionalString(item?.kioskId),
    kioskName: toOptionalString(item?.kioskName),
    entryType:
      item?.entryType === "REDEEMED_CASH" || item?.entryType === "REDEEMED_PROGRAM"
        ? item.entryType
        : "EARNED",
    amountCents: getNumber(item?.amountCents),
    runningAvailableCents: getNumber(item?.runningAvailableCents),
    details: item?.details && typeof item.details === "object" ? item.details : undefined,
  };
};

export const getKioskPayoutLedger = async (
  params?: {
    ownerEmail?: string;
    kioskId?: string;
    fromDate?: string;
    toDate?: string;
  },
): Promise<KioskPayoutLedgerResponse> => {
  try {
    const query = new URLSearchParams();
    if (params?.ownerEmail?.trim()) {
      query.set("ownerEmail", params.ownerEmail.trim());
    }
    if (params?.kioskId?.trim()) {
      query.set("kioskId", params.kioskId.trim());
    }
    if (params?.fromDate?.trim()) {
      query.set("fromDate", params.fromDate.trim());
    }
    if (params?.toDate?.trim()) {
      query.set("toDate", params.toDate.trim());
    }

    const response = await customFetch.get(
      `/kiosk-billing/payout-ledger${query.toString() ? `?${query.toString()}` : ""}`,
    );

    if (response?.status !== HttpStatusCode.Ok) {
      throw new Error("Failed to fetch payout ledger");
    }

    const payload = response.data || {};
    const entriesSource = Array.isArray(payload?.entries) ? payload.entries : [];

    return {
      ownerEmail: toOptionalString(payload?.ownerEmail) || "",
      kioskId: toOptionalString(payload?.kioskId),
      summary: {
        totalEarnedCents: getNumber(payload?.summary?.totalEarnedCents),
        totalRedeemedCents: getNumber(payload?.summary?.totalRedeemedCents),
        availableBalanceCents: getNumber(payload?.summary?.availableBalanceCents),
      },
      entryCount: getNumber(payload?.entryCount) || entriesSource.length,
      entries: entriesSource.map(mapPayoutLedgerEntry),
    };
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, "Failed to fetch payout ledger"));
  }
};

export const redeemKioskPayout = async (payload: {
  ownerEmail?: string;
  kioskId?: string;
  amountCents: number;
  mode: "CASH" | "PROGRAM";
  campaignId?: number;
  payoutProgramKey?: string;
  payoutProgramName?: string;
  notes?: string;
}) => {
  try {
    const response = await customFetch.post("/kiosk-billing/payout-ledger/redeem", payload);

    if (response?.status !== HttpStatusCode.Created && response?.status !== HttpStatusCode.Ok) {
      throw new Error("Failed to redeem payout");
    }

    return response.data;
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, "Failed to redeem payout"));
  }
};

export const getOwnerPayoutProfile = async (ownerEmail?: string): Promise<OwnerPayoutProfile | null> => {
  try {
    const query = new URLSearchParams();
    if (ownerEmail?.trim()) {
      query.set("ownerEmail", ownerEmail.trim());
    }

    const response = await customFetch.get(
      `/kiosk-billing/payout-profile${query.toString() ? `?${query.toString()}` : ""}`,
    );

    if (response?.status !== HttpStatusCode.Ok) {
      throw new Error("Failed to fetch payout profile");
    }

    return response.data || null;
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, "Failed to fetch payout profile"));
  }
};

export const upsertOwnerPayoutProfile = async (payload: {
  ownerEmail?: string;
  methodType: "BANK" | "STRIPE";
  bankAccountHolderName?: string;
  bankName?: string;
  bankAccountNumber?: string;
  bankSortCode?: string;
  bankIban?: string;
  bankSwiftBic?: string;
  bankCountry?: string;
  stripeAccountId?: string;
  stripeAutoPayoutEnabled?: boolean;
}): Promise<OwnerPayoutProfile | null> => {
  try {
    const response = await customFetch.post("/kiosk-billing/payout-profile", payload);

    if (response?.status !== HttpStatusCode.Created && response?.status !== HttpStatusCode.Ok) {
      throw new Error("Failed to save payout profile");
    }

    return response.data || null;
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, "Failed to save payout profile"));
  }
};

export const listOwnerPayoutRequests = async (params?: {
  ownerEmail?: string;
  kioskId?: string;
  status?: OwnerPayoutRequestStatus;
}): Promise<OwnerPayoutRequest[]> => {
  try {
    const response = await customFetch.get("/kiosk-billing/payout-requests", {
      params: {
        ownerEmail: params?.ownerEmail?.trim() || undefined,
        kioskId: params?.kioskId?.trim() || undefined,
        status: params?.status || undefined,
      },
    });

    if (response?.status !== HttpStatusCode.Ok) {
      throw new Error("Failed to load payout requests");
    }

    return Array.isArray(response.data) ? (response.data as OwnerPayoutRequest[]) : [];
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, "Failed to load payout requests"));
  }
};

export const createOwnerPayoutRequest = async (payload: {
  ownerEmail?: string;
  kioskId?: string;
  amountCents: number;
  requestType: OwnerPayoutRequestType;
  notes?: string;
}): Promise<{ message: string; request: OwnerPayoutRequest | null; availableBalanceCents: number }> => {
  try {
    const response = await customFetch.post("/kiosk-billing/payout-requests", payload);

    if (response?.status !== HttpStatusCode.Created && response?.status !== HttpStatusCode.Ok) {
      throw new Error("Failed to create payout request");
    }

    return response.data;
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, "Failed to create payout request"));
  }
};

export const updateOwnerPayoutRequestStatus = async (
  requestId: number,
  payload: { status: OwnerPayoutRequestStatus; adminNote?: string },
): Promise<{ message: string; request: OwnerPayoutRequest | null }> => {
  try {
    const response = await customFetch.post(`/kiosk-billing/payout-requests/${requestId}/status`, payload);

    if (response?.status !== HttpStatusCode.Created && response?.status !== HttpStatusCode.Ok) {
      throw new Error("Failed to update payout request status");
    }

    return response.data;
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, "Failed to update payout request status"));
  }
};
